/**
 * HighlightsSection.jsx - Customize > Highlights (round 4, 10/9/26). Each
 * rule is a card that reads like a sentence ("PO $ is zero → row yellow,
 * red bold text") beside a swatch of exactly how it looks, with a switch to
 * turn it off without deleting it, Edit and Delete; drag a card to change
 * the order. The order matters and the panel says how: the rules are tried
 * top to bottom, and the first one that matches decides a cell's look (and
 * the first row rule a row's) - a cell rule on top of its row's.
 * Adding or editing is one inline form: column -> condition -> value ->
 * fill -> text colour -> bold -> cell or whole row.
 */
import { useState } from 'react';
import { Button } from '../../ui';
import { HIGHLIGHT_COLORS, FONT_COLORS, HIGHLIGHT_OPS, opsFor, ruleSentence, fillOf, fontOf } from '../highlights';
import { usePointerSort, moveTo } from '../usePointerSort';
import { Switch, Segmented, ColorChoice, SectionHead } from './controls';

const SELECT = 'border border-border-strong rounded px-2 py-1 text-[12px] bg-surface';

function RuleForm({ defs, initial, onSave, onCancel }) {
  const ruleCols = defs.filter((d) => d.kind);
  const [rule, setRule] = useState(() =>
    initial
      ? // a rule saved before text colours had only a fill (none set = yellow)
        { ...initial, color: initial.color ?? (initial.font ? 'none' : 'yellow'), font: initial.font || 'none', bold: !!initial.bold }
      : { col: ruleCols[0].col, op: 'zero', value: '', color: 'yellow', font: 'none', bold: false, scope: 'row' }
  );
  const kind = (defs.find((d) => d.col === rule.col) || {}).kind || 'text';
  const ops = opsFor(kind);
  const op = ops.includes(rule.op) ? rule.op : ops[0];
  const input = HIGHLIGHT_OPS[op].input;
  const set = (patch) => setRule((r) => ({ ...r, ...patch }));
  const hasLook = rule.color !== 'none' || rule.font !== 'none';
  const ready = hasLook && (!input || String(rule.value ?? '').trim() !== '');
  const save = (e) => {
    e.preventDefault();
    if (!ready) return;
    // 'always' paints the column's cells; on the row it would paint every row
    const out = { col: rule.col, op, color: rule.color, scope: op === 'always' ? 'cell' : rule.scope };
    if (rule.font !== 'none') out.font = rule.font;
    if (rule.bold) out.bold = true;
    if (initial && initial.off) out.off = true;
    if (input === 'number') out.value = Number(rule.value);
    else if (input) out.value = String(rule.value).trim();
    if (input === 'number' && Number.isNaN(out.value)) return;
    onSave(out);
  };
  const preview = {
    col: rule.col,
    op,
    value: rule.value,
    color: rule.color,
    font: rule.font === 'none' ? undefined : rule.font,
    bold: rule.bold,
    scope: rule.scope,
  };
  return (
    <form onSubmit={save} className="border border-gat-green/40 rounded-lg p-3 space-y-2.5 bg-surface-2" data-no-drag="">
      <div className="flex items-center gap-2 flex-wrap text-[12px]">
        <span className="font-semibold">When</span>
        <select aria-label="Highlight column" className={SELECT} value={rule.col} onChange={(e) => set({ col: e.target.value })}>
          {ruleCols.map((d) => (
            <option key={d.col} value={d.col}>
              {d.label}
            </option>
          ))}
        </select>
        <select aria-label="Highlight condition" className={SELECT} value={op} onChange={(e) => set({ op: e.target.value })}>
          {ops.map((o) => (
            <option key={o} value={o}>
              {HIGHLIGHT_OPS[o].label}
            </option>
          ))}
        </select>
        {input && (
          <input
            aria-label="Highlight value"
            className={`${SELECT} w-32`}
            type={input === 'date' ? 'date' : input === 'number' ? 'number' : 'text'}
            step="any"
            value={rule.value ?? ''}
            onChange={(e) => set({ value: e.target.value })}
            placeholder={op === 'lastDays' ? 'days' : ''}
          />
        )}
      </div>
      <div className="space-y-1.5 text-[12px]">
        <ColorChoice label="Highlight fill" presets={HIGHLIGHT_COLORS} value={rule.color} onChange={(color) => set({ color })} />
        <ColorChoice label="Highlight text" presets={FONT_COLORS} value={rule.font} onChange={(font) => set({ font })} text />
      </div>
      <div className="flex items-center gap-3 flex-wrap text-[12px]">
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={rule.bold} onChange={(e) => set({ bold: e.target.checked })} /> <b>Bold</b>
        </label>
        <Segmented
          label="Highlight what"
          value={rule.scope}
          onChange={(scope) => set({ scope })}
          options={[
            ['cell', 'The cell'],
            ['row', 'The whole row'],
          ]}
        />
      </div>
      <div className="flex items-center gap-2 text-[11.5px] text-text-muted">
        Looks like
        <span
          className="px-2 py-0.5 rounded border border-border-strong"
          style={{ background: fillOf(preview) || '#fff', color: fontOf(preview) || '#111827', fontWeight: rule.bold ? 800 : 500 }}
        >
          1,250.00
        </span>
      </div>
      {!hasLook && <div className="text-[11.5px] text-status-lost">Pick a fill or a text colour.</div>}
      <div className="flex gap-2">
        <Button size="sm" type="submit" disabled={!ready}>
          {initial ? 'Save rule' : 'Add rule'}
        </Button>
        <Button size="sm" variant="ghost" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function HighlightsSection({ view, defs }) {
  const [editing, setEditing] = useState(null); // index of the rule being edited, 'new', or null
  const byId = Object.fromEntries(defs.map((d) => [d.col, d]));
  const labelOf = (c) => (byId[c] ? byId[c].label : c);
  const rules = view.highlights;
  const sort = usePointerSort({
    axis: 'y',
    onDrop: (key, to) => {
      const order = moveTo(
        rules.map((_, i) => String(i)),
        key,
        to
      );
      view.setHighlights(order.map((k) => rules[Number(k)]));
    },
  });
  const saveRule = (rule) => {
    view.setHighlights(editing === 'new' ? [...rules, rule] : rules.map((r, i) => (i === editing ? rule : r)));
    setEditing(null);
  };
  const setRule = (i, patch) => view.setHighlights(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div>
      <SectionHead
        title="Highlights"
        hint="Colour cells or whole rows when a value matches. Rules are tried top to bottom: the first that matches decides a cell's look (and the first row rule a row's). Drag a rule to change the order."
        onReset={rules.length ? () => view.resetSection('highlights') : undefined}
        resetLabel="Remove all"
      />
      {rules.length === 0 && editing == null && (
        <p className="text-[12px] text-text-muted m-0 mb-2">No highlights yet - e.g. highlight a row yellow when PO $ is zero.</p>
      )}
      <ul
        ref={sort.ref}
        onPointerDown={editing == null ? sort.onPointerDown : undefined}
        aria-label="Highlight rules"
        className="m-0 p-0 list-none space-y-2"
      >
        {rules.map((r, i) =>
          editing === i ? (
            <li key={`edit-${i}`}>
              <RuleForm defs={defs} initial={r} onSave={saveRule} onCancel={() => setEditing(null)} />
            </li>
          ) : (
            <li
              key={`${r.col}-${r.op}-${i}`}
              data-sort-key={String(i)}
              data-sort-label={ruleSentence(r, labelOf)}
              className={`flex items-center gap-2 border border-border rounded-lg px-2.5 py-2 bg-surface text-[12px] ${r.off ? 'opacity-55' : ''}`}
            >
              <span className="cursor-grab text-text-muted" aria-hidden="true" title="Drag to change the order">
                ⋮⋮
              </span>
              <span
                className="w-9 h-6 rounded border border-border-strong shrink-0 inline-flex items-center justify-center text-[11px]"
                style={{ background: fillOf(r) || '#fff', color: fontOf(r) || '#111827', fontWeight: r.bold ? 800 : 500 }}
              >
                Aa
              </span>
              <span className="flex-1 leading-snug">{ruleSentence(r, labelOf)}</span>
              <Switch small on={!r.off} onChange={(on) => setRule(i, { off: !on })} label={`Rule ${i + 1} on`} />
              <Button size="sm" variant="ghost" onClick={() => setEditing(i)}>
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => view.setHighlights(rules.filter((_, j) => j !== i))}
                aria-label={`Delete highlight ${i + 1}`}
              >
                Delete
              </Button>
            </li>
          )
        )}
      </ul>
      {editing === 'new' ? (
        <div className="mt-2">
          <RuleForm defs={defs} onSave={saveRule} onCancel={() => setEditing(null)} />
        </div>
      ) : (
        editing == null && (
          <div className="mt-3">
            <Button size="sm" variant="secondary" onClick={() => setEditing('new')}>
              ＋ Add highlight
            </Button>
          </div>
        )
      )}
    </div>
  );
}
